<template>
  <div class="blog-page">
    <!-- 动态背景 -->
    <div class="animated-background">
      <div class="floating-shapes">
        <div class="shape shape-1"></div>
        <div class="shape shape-2"></div>
        <div class="shape shape-3"></div>
      </div>
      <div class="grid-overlay"></div>
    </div>

    <!-- 导航栏 -->
    <nav class="top-nav">
      <div class="nav-container">
        <router-link to="/" class="nav-brand">
          <div class="brand-icon">
            <svg viewBox="0 0 24 24" width="32" height="32">
              <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
            </svg>
          </div>
          <span class="brand-text">{{ $t('nav.brand') }}</span>
        </router-link>
        <div class="nav-actions">
          <div class="language-switcher">
            <button 
              @click="toggleLanguage" 
              class="language-btn"
              :title="$t('common.language')"
            >
              <svg viewBox="0 0 24 24" width="18" height="18">
                <path fill="currentColor" d="M12.87,15.07L10.33,12.56L10.36,12.53C12.1,10.59 13.34,8.36 14.07,6H17V4H10V2H8V4H1V6H12.17C11.5,7.92 10.44,9.75 9,11.35C8.07,10.32 7.3,9.19 6.69,8H4.69C5.42,9.63 6.42,11.17 7.67,12.56L2.58,17.58L4,19L9,14L12.11,17.11L12.87,15.07M18.5,10H16.5L12,22H14L15.12,19H19.87L21,22H23L18.5,10M15.88,17L17.5,12.67L19.12,17H15.88Z"/>
              </svg>
              {{ currentLanguage === 'zh' ? 'EN' : '中' }}
            </button>
          </div>
          <router-link to="/" class="nav-btn">{{ $t('pricing.nav.home') }}</router-link>
          <router-link to="/pricing" class="nav-btn">{{ $t('pricing.nav.pricing') }}</router-link>
        </div>
      </div>
    </nav>

    <!-- 主要内容 -->
    <div class="main-container">
      <!-- Hero 区域 -->
      <section class="hero-section">
        <div class="hero-content">
          <h1 class="hero-title">{{ $t('blog.title') }}</h1>
          <p class="hero-description">{{ $t('blog.subtitle') }}</p>
        </div>
      </section>

      <!-- 搜索和过滤 -->
      <section class="filter-section">
        <div class="filter-container">
          <div class="search-box">
            <input 
              v-model="searchQuery"
              type="text" 
              :placeholder="$t('blog.searchPlaceholder')"
              class="search-input"
            >
            <svg class="search-icon" viewBox="0 0 24 24" width="20" height="20">
              <path fill="currentColor" d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/>
            </svg>
          </div>
          
          <div class="category-filter">
            <select v-model="selectedCategory" class="category-select">
              <option value="">{{ $t('blog.allCategories') }}</option>
              <option v-for="category in categories" :key="category" :value="category">
                {{ category }}
              </option>
            </select>
          </div>
        </div>
      </section>

      <!-- 博客文章列表 -->
      <section class="posts-section">
        <div class="posts-container">
          <div v-if="filteredPosts.length === 0" class="no-posts">
            <div class="no-posts-icon">
              <svg viewBox="0 0 24 24" width="64" height="64">
                <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
              </svg>
            </div>
            <p>{{ $t('blog.noPostsFound') }}</p>
          </div>
          
          <div v-else class="posts-grid">
            <article 
              v-for="post in filteredPosts" 
              :key="post.id"
              class="post-card"
              @click="goToPost(post.id)"
            >
              <div class="post-header">
                <div class="post-meta">
                  <span class="post-category">{{ post.category }}</span>
                  <span class="post-date">{{ formatDate(post.publishedAt) }}</span>
                </div>
                <div v-if="post.featured" class="featured-badge">
                  <svg viewBox="0 0 24 24" width="16" height="16">
                    <path fill="currentColor" d="M12,17.27L18.18,21L16.54,13.97L22,9.24L14.81,8.62L12,2L9.19,8.62L2,9.24L7.46,13.97L5.82,21L12,17.27Z"/>
                  </svg>
                </div>
              </div>
              
              <h2 class="post-title">{{ getLocalizedContent(post.title) }}</h2>
              <p class="post-excerpt">{{ getLocalizedContent(post.excerpt) }}</p>
              
              <div class="post-footer">
                <div class="post-tags">
                  <span 
                    v-for="tag in post.tags.slice(0, 3)" 
                    :key="tag"
                    class="tag"
                  >
                    {{ tag }}
                  </span>
                </div>
                <div class="post-read-time">
                  {{ post.readTime }} min read
                </div>
              </div>
              
              <div class="post-cta">
                <span>{{ $t('blog.readMore') }}</span>
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path fill="currentColor" d="M4,11V13H16L10.5,18.5L11.92,19.92L19.84,12L11.92,4.08L10.5,5.5L16,11H4Z"/>
                </svg>
              </div>
            </article>
          </div>
        </div>
      </section>
    </div>

    <!-- 页脚 -->
    <footer class="site-footer">
      <div class="footer-container">
        <div class="footer-brand">
          <div class="brand-icon">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
            </svg>
          </div>
          <span>{{ $t('footer.brand') }}</span>
        </div>
        <div class="footer-links">
          <router-link to="/blog">{{ $t('footer.docs') }}</router-link>
          <a href="#">{{ $t('footer.about') }}</a>
          <a href="mailto:fn9170@gmail.com">{{ $t('footer.contact') }}</a>
        </div>
        <div class="footer-copyright">
          {{ $t('footer.copyright') }}
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { changeLanguage, getCurrentLanguage } from '@/i18n'
import blogManager from '@/utils/blogManager'

export default {
  name: 'BlogPage',
  data() {
    return {
      currentLanguage: getCurrentLanguage(),
      posts: [],
      categories: [],
      searchQuery: '',
      selectedCategory: ''
    }
  },
  computed: {
    filteredPosts() {
      let filtered = this.posts
      
      // 按搜索查询过滤
      if (this.searchQuery) {
        filtered = blogManager.searchPosts(this.searchQuery, this.currentLanguage)
      }
      
      // 按分类过滤
      if (this.selectedCategory) {
        filtered = filtered.filter(post => post.category === this.selectedCategory)
      }
      
      return filtered
    }
  },
  methods: {
    toggleLanguage() {
      const newLanguage = this.currentLanguage === 'en' ? 'zh' : 'en'
      this.currentLanguage = changeLanguage(newLanguage)
      this.updateSEO()
    },
    
    getLocalizedContent(content) {
      return blogManager.getLocalizedContent(content, this.currentLanguage)
    },
    
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString(this.currentLanguage === 'zh' ? 'zh-CN' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    
    goToPost(postId) {
      this.$router.push(`/blog/${postId}`)
    },
    
    updateSEO() {
      document.title = this.$t('blog.seo.title')
      
      const updateMetaTag = (name, content) => {
        let element = document.querySelector(`meta[name="${name}"]`)
        if (!element) {
          element = document.createElement('meta')
          element.setAttribute('name', name)
          document.head.appendChild(element)
        }
        element.setAttribute('content', content)
      }
      
      updateMetaTag('description', this.$t('blog.seo.description'))
      updateMetaTag('keywords', this.$t('blog.seo.keywords'))
    }
  },
  
  mounted() {
    this.posts = blogManager.getAllPosts()
    this.categories = blogManager.getAllCategories()
    this.updateSEO()
  },
  
  watch: {
    '$i18n.locale'() {
      this.updateSEO()
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.blog-page {
  min-height: 100vh;
  background: #0f172a;
  position: relative;
  overflow-x: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* ===== 动态背景 ===== */
.animated-background {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  background: 
    radial-gradient(circle at 20% 10%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 80% 90%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 60%, rgba(6, 182, 212, 0.1) 0%, transparent 50%),
    linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
}

.floating-shapes {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.shape {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
  filter: blur(40px);
  animation: float 20s ease-in-out infinite;
}

.shape-1 {
  width: 300px;
  height: 300px;
  top: 10%;
  left: 10%;
  animation-delay: 0s;
}

.shape-2 {
  width: 200px;
  height: 200px;
  top: 60%;
  right: 10%;
  animation-delay: 5s;
}

.shape-3 {
  width: 150px;
  height: 150px;
  bottom: 20%;
  left: 50%;
  animation-delay: 10s;
}

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-20px) rotate(120deg); }
  66% { transform: translateY(20px) rotate(240deg); }
}

.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px);
  background-size: 50px 50px;
  opacity: 0.3;
  animation: gridMove 40s linear infinite;
}

@keyframes gridMove {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

/* ===== 导航栏 ===== */
.top-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(59, 130, 246, 0.1);
  padding: 1rem 0;
}

.nav-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
}

.brand-icon {
  color: #3b82f6;
  transition: transform 0.3s ease;
}

.brand-icon:hover {
  transform: scale(1.1);
}

.brand-text {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.nav-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.language-switcher {
  position: relative;
}

.language-btn {
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #e2e8f0;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  min-width: 60px;
  justify-content: center;
}

.language-btn:hover {
  background: rgba(59, 130, 246, 0.2);
  border-color: #3b82f6;
  transform: translateY(-1px);
}

.nav-btn {
  background: transparent;
  border: 1px solid rgba(59, 130, 246, 0.3);
  color: #e2e8f0;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none !important;
}

.nav-btn:hover {
  background: rgba(59, 130, 246, 0.1);
  border-color: #3b82f6;
}

/* ===== 主容器 ===== */
.main-container {
  position: relative;
  z-index: 1;
  padding-top: 100px;
}

/* ===== Hero 区域 ===== */
.hero-section {
  padding: 4rem 2rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: #f1f5f9;
  letter-spacing: -0.02em;
}

.hero-description {
  font-size: 1.2rem;
  line-height: 1.7;
  color: #94a3b8;
  max-width: 600px;
  margin: 0 auto;
}

/* ===== 过滤区域 ===== */
.filter-section {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.filter-container {
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.search-input {
  width: 100%;
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
  padding: 1rem 1rem 1rem 3rem;
  color: #f1f5f9;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-input::placeholder {
  color: #64748b;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
}

.category-select {
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
  padding: 1rem 1.5rem;
  color: #f1f5f9;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 200px;
}

.category-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.category-select option {
  background: #1e293b;
  color: #f1f5f9;
}

/* ===== 文章区域 ===== */
.posts-section {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.no-posts {
  text-align: center;
  padding: 4rem 2rem;
  color: #64748b;
}

.no-posts-icon {
  margin-bottom: 1rem;
  opacity: 0.5;
}

.posts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2rem;
}

.post-card {
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 20px;
  padding: 2rem;
  backdrop-filter: blur(20px);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.post-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6, #06b6d4);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.post-card:hover::before {
  opacity: 1;
}

.post-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border-color: rgba(59, 130, 246, 0.4);
}

.post-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.post-meta {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.post-category {
  background: rgba(59, 130, 246, 0.2);
  color: #3b82f6;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  width: fit-content;
}

.post-date {
  color: #64748b;
  font-size: 0.9rem;
}

.featured-badge {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
  padding: 0.5rem;
  border-radius: 8px;
}

.post-title {
  color: #f1f5f9;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  line-height: 1.3;
}

.post-excerpt {
  color: #94a3b8;
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.post-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.post-tags {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.tag {
  background: rgba(139, 92, 246, 0.1);
  color: #8b5cf6;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.post-read-time {
  color: #64748b;
  font-size: 0.9rem;
}

.post-cta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #3b82f6;
  font-weight: 600;
  font-size: 0.9rem;
}

/* ===== 页脚 ===== */
.site-footer {
  background: rgba(15, 23, 42, 0.9);
  border-top: 1px solid rgba(59, 130, 246, 0.1);
  padding: 3rem 2rem 2rem;
  position: relative;
  z-index: 1;
  margin-top: 4rem;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
}

.footer-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #f1f5f9;
  font-weight: 600;
}

.footer-brand .brand-icon {
  color: #3b82f6;
}

.footer-links {
  display: flex;
  gap: 2rem;
}

.footer-links a {
  color: #94a3b8;
  text-decoration: none;
  transition: color 0.3s ease;
}

.footer-links a:hover {
  color: #3b82f6;
}

.footer-copyright {
  color: #64748b;
  font-size: 0.9rem;
}

/* ===== 响应式设计 ===== */
@media (max-width: 768px) {
  .nav-container {
    padding: 0 1rem;
  }
  
  .hero-title {
    font-size: 2.5rem;
  }
  
  .filter-container {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-box {
    max-width: none;
  }
  
  .posts-grid {
    grid-template-columns: 1fr;
  }
  
  .footer-container {
    flex-direction: column;
    text-align: center;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .post-card {
    padding: 1.5rem;
  }
  
  .post-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}
</style>